/**
 * `numbering.*` — Settings → Numbering wire surface.
 *
 * Three procedures (all `tenantedProcedure`):
 *   - `get`         — read current state of the 4 V1.0 doc-type sequences
 *                     for the current FY.
 *   - `update`      — patch prefix and/or padding for a single doc type.
 *   - `setNextSeq`  — gated "Continue from existing series" affordance
 *                     for Tally-migration day-1.
 *
 * Enums + char limits mirror the domain constants exactly (kept in sync
 * by the unit tests; both sides import the same regexes).
 */
import { z } from "zod";
import { uuid } from "../_common.js";

/** V1.0 doc types — the subset of `DOCUMENT_TYPES` that ships in V1.0. */
export const v1NumberingDocumentType = z.enum([
  "tax-invoice",
  "credit-note",
  "receipt",
  "vendor-payment",
]);
export type V1NumberingDocumentType = z.infer<typeof v1NumberingDocumentType>;

/** Padding selector — 3..6. */
export const paddingOption = z.union([
  z.literal(3),
  z.literal(4),
  z.literal(5),
  z.literal(6),
]);

/** Prefix regex — kept in sync with `domain ▸ PREFIX_REGEX`. */
export const prefixField = z
  .string()
  .trim()
  .min(1)
  .max(8)
  .regex(/^[A-Z0-9-]+$/i, "1–8 uppercase letters, digits, or dashes");

// ── get ───────────────────────────────────────────────────────────

export const getInput = z.object({});

const settingsRow = z.object({
  documentType: v1NumberingDocumentType,
  label: z.string(),
  fy: z.string(),
  poolId: uuid.nullable(),
  prefix: z.string(),
  padding: z.number().int(),
  nextSeq: z.number().int(),
  canSetNextSeq: z.boolean(),
});

export const getOutput = z.object({
  fy: z.string(),
  rows: z.array(settingsRow),
});

// ── update ────────────────────────────────────────────────────────

export const updateInput = z
  .object({
    documentType: v1NumberingDocumentType,
    prefix: prefixField.optional(),
    padding: paddingOption.optional(),
  })
  .refine((v) => v.prefix !== undefined || v.padding !== undefined, {
    message: "Provide at least one field (prefix or padding) to update.",
  });

export const updateOutput = z.object({
  documentType: v1NumberingDocumentType,
  poolId: uuid,
  prefix: z.string(),
  padding: z.number().int(),
  nextSeq: z.number().int(),
  fy: z.string(),
});

// ── setNextSeq ────────────────────────────────────────────────────

export const setNextSeqInput = z.object({
  documentType: v1NumberingDocumentType,
  /** Positive integer. The domain rejects when `currentNextSeq > 1` (gate). */
  nextSeq: z.number().int().positive(),
});

export const setNextSeqOutput = z.object({
  documentType: v1NumberingDocumentType,
  poolId: uuid,
  nextSeq: z.number().int(),
  fy: z.string(),
});
